import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useField } from 'react-final-form';

import SectionTitle from '../../components/section-title/SectionTitle';
import SelectedMediaHero from './components/selected-media-hero/SelectedMediaHero';
import SelectedMediaFilmstrip from './components/selected-media-filmstrip/SelectedMediaFilmstrip';
import SelectedMediaHeroDropzone from './components/selected-media-hero/SelectedMediaHeroDropzone';
import SelectedMediaEmpty from './components/selected-media-empty/SelectedMediaEmpty';
import SelectedMediaUploadButton from './components/selected-media-upload/SelectedMediaUploadButton';
import MediaPreviewModal from '../../components/media-preview-modal/MediaPreviewModal';
import MediaTypeConflictModal from '../../components/media-type-conflict-modal/MediaTypeConflictModal';
import { STORY_FORM_FIELDS, STORY_MEDIA_RESTRICTIONS } from '../../constants';
import { setSelectedMedia, setSelectedMediaActiveId } from '../../../../redux/story/storyReducer';
import { uploadStoryMedia } from '../../../../redux/story/storyActions';
import { useProject } from '../../../../context/ProjectContext';
import useStoryMediaTypeConflict from '../../hooks/useStoryMediaTypeConflict';
import { createVideoPosterFromFile } from './utils/createVideoPosterFromFile';
import { MEDIA_TYPES } from '../../../../constants';
import {
  createUploadingStoryMedia,
  filterFilesByPrimaryType,
  getStoryMediaId,
  isMediaTypeConflict,
  mediaTypeFromFile,
  normalizeFromLinkedContent,
} from '../../media/storyMediaItem';

import scss from './SelectedMedia.module.scss';

function SelectedMedia() {
  const { t } = useTranslation('story');
  const dispatch = useDispatch();
  const { actions } = useProject();

  const { input: mediaInput } = useField(STORY_FORM_FIELDS.MEDIA);
  const selectedMedia = useSelector((state) => state.story.selectedMedia);
  const selectedMediaActiveId = useSelector((state) => state.story.selectedMediaActiveId);

  const [activeIndex, setActiveIndex] = useState(0);
  const [previewMedia, setPreviewMedia] = useState(null);

  const items = useMemo(() => mediaInput.value ?? [], [mediaInput.value]);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const { isOpen, incomingType, requestAction, confirm, cancel } = useStoryMediaTypeConflict({ selectedItems: items });

  const updateItems = useCallback(
    (nextItems) => {
      itemsRef.current = nextItems;
      mediaInput.onChange(nextItems);
      dispatch(setSelectedMedia(nextItems));
    },
    [dispatch, mediaInput],
  );

  useEffect(() => {
    const formItems = mediaInput.value ?? [];
    const formIds = formItems.map(getStoryMediaId).join(',');
    const reduxIds = selectedMedia.map(getStoryMediaId).join(',');

    if (formIds === reduxIds) {
      return;
    }

    mediaInput.onChange(selectedMedia);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMedia]);

  useEffect(() => {
    if (activeIndex >= items.length && items.length > 0) {
      setActiveIndex(items.length - 1);
    }

    if (items.length === 0) {
      setActiveIndex(0);
    }
  }, [activeIndex, items.length]);

  useEffect(() => {
    if (!selectedMediaActiveId || !items.length) {
      return;
    }

    const index = items.findIndex((item) => getStoryMediaId(item) === selectedMediaActiveId);

    if (index !== -1) {
      setActiveIndex((currentIndex) => (currentIndex === index ? currentIndex : index));
    }
  }, [items, selectedMediaActiveId]);

  useEffect(() => {
    if (!items.length) {
      if (selectedMediaActiveId) {
        dispatch(setSelectedMediaActiveId(null));
      }
      return;
    }

    const safeIndex = activeIndex >= items.length ? items.length - 1 : activeIndex;
    dispatch(setSelectedMediaActiveId(getStoryMediaId(items[safeIndex])));
    // selectedMediaActiveId intentionally omitted — effect writes it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, dispatch, items]);

  const activeId = getStoryMediaId(items[activeIndex]);

  const performUpload = useCallback(
    async (fileList, { replace = false } = {}) => {
      const posterUrls = await Promise.all(
        fileList.map((file) =>
          mediaTypeFromFile(file) === MEDIA_TYPES.VIDEO ? createVideoPosterFromFile(file) : Promise.resolve(null),
        ),
      );

      const placeholders = fileList.map((file, index) =>
        createUploadingStoryMedia({
          fileName: file.name,
          type: mediaTypeFromFile(file),
          posterUrl: posterUrls[index] ?? undefined,
        }),
      );
      const placeholderIds = new Set(placeholders.map(getStoryMediaId));
      const baseItems = replace ? [] : itemsRef.current;
      const nextItems = [...baseItems, ...placeholders];

      updateItems(nextItems);
      setActiveIndex(nextItems.length - 1);

      const result = await actions('uploadStoryMedia', { files: fileList });
      const withoutPlaceholders = itemsRef.current.filter((item) => !placeholderIds.has(getStoryMediaId(item)));

      if (!uploadStoryMedia.fulfilled.match(result)) {
        posterUrls.filter(Boolean).forEach((url) => URL.revokeObjectURL(url));
        updateItems(replace ? [] : withoutPlaceholders);
        return;
      }

      const uploadedItems = result.payload.map((linked, index) =>
        normalizeFromLinkedContent({
          id: linked.id,
          type: linked.type,
          link: linked.link,
          fileName: placeholders[index]?.fileName,
          posterUrl: placeholders[index]?.posterUrl,
        }),
      );

      updateItems([...withoutPlaceholders, ...uploadedItems]);
      setActiveIndex(withoutPlaceholders.length + uploadedItems.length - 1);
    },
    [actions, updateItems],
  );

  const handleUploadFiles = (files) => {
    if (STORY_MEDIA_RESTRICTIONS.DISABLE_CUSTOM_UPLOAD) {
      return;
    }

    const fileList = filterFilesByPrimaryType(files);

    if (!fileList.length) {
      return;
    }

    const incomingType = mediaTypeFromFile(fileList[0]);

    requestAction(incomingType, () => {
      performUpload(fileList, { replace: isMediaTypeConflict(itemsRef.current, incomingType) });
    });
  };

  const handleRemove = (sourceId) => {
    const removed = itemsRef.current.find((item) => getStoryMediaId(item) === sourceId);

    if (removed?.posterUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(removed.posterUrl);
    }

    const nextItems = itemsRef.current.filter((item) => getStoryMediaId(item) !== sourceId);
    updateItems(nextItems);

    const removedIndex = items.findIndex((item) => getStoryMediaId(item) === sourceId);
    if (removedIndex <= activeIndex && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const handleReorder = (nextItems) => {
    updateItems(nextItems);

    const nextActiveIndex = nextItems.findIndex((item) => getStoryMediaId(item) === activeId);
    if (nextActiveIndex !== -1) {
      setActiveIndex(nextActiveIndex);
    }
  };

  const handleSelect = (sourceId) => {
    const index = items.findIndex((item) => getStoryMediaId(item) === sourceId);

    if (index !== -1) {
      setActiveIndex(index);
    }
  };

  const handleActiveIndexChange = (index) => {
    setActiveIndex(index);
  };

  const handlePreview = (media) => {
    setPreviewMedia(media);
  };

  const handleClosePreview = () => {
    setPreviewMedia(null);
  };

  const hasItems = items.length > 0;
  const showFilmstrip = items.length > 1;

  return (
    <div className={scss.wrapper}>
      <SectionTitle title={t('selectedMedia')}>
        <SelectedMediaUploadButton
          onUpload={handleUploadFiles}
          disabled={STORY_MEDIA_RESTRICTIONS.DISABLE_CUSTOM_UPLOAD}
        />
      </SectionTitle>

      <div className={scss.content}>
        <SelectedMediaHeroDropzone onUpload={handleUploadFiles} disabled={STORY_MEDIA_RESTRICTIONS.DISABLE_CUSTOM_DROP}>
          {!hasItems ? (
            <SelectedMediaEmpty />
          ) : (
            <>
              <SelectedMediaHero
                items={items}
                activeIndex={activeIndex}
                onActiveIndexChange={handleActiveIndexChange}
                onPreview={handlePreview}
                onRemoveSingle={handleRemove}
              />

              {showFilmstrip ? (
                <SelectedMediaFilmstrip
                  items={items}
                  activeId={activeId}
                  onSelect={handleSelect}
                  onRemove={handleRemove}
                  onReorder={handleReorder}
                />
              ) : null}
            </>
          )}
        </SelectedMediaHeroDropzone>
      </div>

      <MediaPreviewModal isOpen={previewMedia} onClose={handleClosePreview} media={previewMedia} />

      <MediaTypeConflictModal
        isOpen={isOpen}
        incomingType={incomingType}
        onClose={cancel}
        onCancel={cancel}
        onConfirm={confirm}
      />
    </div>
  );
}

export default SelectedMedia;
